import { SupportedLanguages } from 'src/modules/locales/types';
import { LANGUAGE_CONFIG } from '../config';
import { Config, LanguageConfig } from '../types';
import { COUNTRY_TO_LANG } from 'src/modules/locales/config';
import { setupCurrencyInterceptor } from './setupCurrencyInterceptor';

interface GetLanguageConfigParams {
  isGBR: boolean;
  hasAffiliateLink: boolean;
  language: SupportedLanguages;
  isAuthenticated: boolean;
}

/**
 * Функция `getLanguageConfig` возвращает конфигурацию языка и валюты на основе переданных параметров.
 * В зависимости от страны пользователя (Великобритания или нет), наличия партнерской ссылки и аутентификации,
 * функция выбирает соответствующую конфигурацию из предопределенного набора `LANGUAGE_CONFIG`.
 * Также функция может настроить перехватчик валюты, если пользователь из Великобритании и у него нет партнерской ссылки.
 *
 * @param {GetLanguageConfigParams} params - Параметры, передаваемые в функцию.
 * @param {boolean} params.isGBR - Флаг, указывающий, является ли пользователь из Великобритании.
 * @param {boolean} params.hasAffiliateLink - Флаг, указывающий, есть ли у пользователя партнерская ссылка.
 * @param {keyof SupportedLanguages} params.language - Язык пользователя.
 * @param {boolean} params.isAuthenticated - Флаг, указывающий, аутентифицирован ли пользователь.
 *
 * @returns {LanguageConfig} - Конфигурация языка и валюты, соответствующая переданным параметрам.
 */

export const getLanguageConfig = ({
  isGBR,
  hasAffiliateLink,
  language: userLanguage,
  isAuthenticated
}: GetLanguageConfigParams): LanguageConfig => {
  const defaultConfig = LANGUAGE_CONFIG.get('default')?.config as Config;

  if (isAuthenticated && isGBR) {
    return {
      language: userLanguage,
      config: { ...(LANGUAGE_CONFIG.get('authorized_uk')?.config as Config) }
    };
  }

  if (isAuthenticated) {
    return { language: userLanguage, config: defaultConfig };
  }

  if (isGBR && !hasAffiliateLink) {
    const clearInterseptors = setupCurrencyInterceptor('EUR');
    return {
      language: userLanguage,
      clearInterseptors,
      config: { ...(LANGUAGE_CONFIG.get('uk_no_referral')?.config as Config) }
    };
  }

  if (isGBR && hasAffiliateLink) {
    return {
      language: userLanguage === COUNTRY_TO_LANG.EN ? COUNTRY_TO_LANG.GB : userLanguage,
      config: { ...(LANGUAGE_CONFIG.get('uk_with_referral')?.config as Config) }
    };
  }

  if (hasAffiliateLink) {
    return {
      language: userLanguage,
      config: LANGUAGE_CONFIG.get('non_uk_with_referral')?.config as Config
    };
  }

  return { language: userLanguage, config: defaultConfig };
};
